'use client';

import { type DocumentType } from '../document/document-types';
import { documentUsesBank } from '../document/field-policy';


import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { mutationHeaders, responseMessage } from '../../components/app-shell';
import { logGenerateDebug } from './generate-debug';
import type { TradeInput } from '../trade/schema';
import { completionFieldLabel, getDocumentCompletion, getSkippedFields, type DocumentCompletion } from './document-completion';

export type { DocumentType } from '../document/document-types';
export type ConversationMessage = Readonly<{
  id: string;
  sequence: number;
  status: string;
  text: string;
  textLength: number;
  jobId: string | null;
  createdAt: string;
  attachments: readonly Readonly<{ id: string; fileName: string; mediaType: string; sizeBytes: number; scanStatus: string; extractedText: string | null }>[];
}>;
export type ConversationProposal = Readonly<{
  id: string;
  messageId: string;
  target: DocumentType;
  status: string;
  operationsJson: unknown;
  appliedOperationsJson?: unknown;
  missingJson: unknown;
  conflictsJson: unknown;
  baseRevision: number | null;
  invalidReason: string | null;
  appliedTradeId: string | null;
  appliedDocumentId: string | null;
  appliedRevision: number | null;
  createdAt: string;
}>;
export type ConversationView = Readonly<{
  conversationId: string;
  documentType: DocumentType;
  templateVersion: string;
  workingRevision: number;
  status: string;
  tradeId: string | null;
  tradeRevisionId: string | null;
  resolvedBankAccountVersionId?: string | null;
  resolvedBankAccountTradeRevision?: number | null;
  shareScopeVersion: number;
  messages: readonly ConversationMessage[];
  proposals: readonly ConversationProposal[];
}>;
export type JobView = Readonly<{
  jobId: string;
  kind: string;
  state: string;
  errorCode: string | null;
  cancelRequestedAt: string | null;
}>;
export type TradeInputView = TradeInput;

type ConversationTradeView = Readonly<{ input: TradeInputView; tradeId: string; revision: number }>;
type ConversationBankContext = Pick<ConversationView, 'tradeId' | 'documentType' | 'resolvedBankAccountVersionId' | 'resolvedBankAccountTradeRevision'>;

export function getConversationCompletionInput(trade: ConversationTradeView | null, conversation: ConversationBankContext | null): TradeInputView | null {
  if (!trade) return null;
  if (trade.input.bankAccountVersionId || !conversation?.resolvedBankAccountVersionId
    || !documentUsesBank(conversation.documentType) || conversation.tradeId !== trade.tradeId
    || conversation.resolvedBankAccountTradeRevision !== trade.revision) return trade.input;
  return { ...trade.input, bankAccountVersionId: conversation.resolvedBankAccountVersionId };
}

type SubmissionAttempt = Readonly<{
  conversationId: string;
  text: string;
  key: string;
  attachmentIdentity: string;
}>;

export function useConversation(): Readonly<{
  conversation: ConversationView | null;
  job: JobView | null;
  proposal: ConversationProposal | null;
  appliedProposal: ConversationProposal | null;
  tradeInput: TradeInputView | null;
  completionInput: TradeInputView | null;
  draft: string;
  documentType: DocumentType;
  pending: boolean;
  error: string | null;
  completion: DocumentCompletion | null;
  changes: readonly string[];
  appliedDocumentEvent: Readonly<{ proposalId: string; documentId: string }> | null;
  consumeAppliedDocumentEvent(): void;
  setDraft(value: string): void;
  setDocumentType(value: DocumentType): void;
  startNew(): void;
  submit(): Promise<void>;
  cancel(): Promise<void>;
  retry(): Promise<void>;
  retrySave(): Promise<void>;
  apply(selectedOperationIds: readonly string[], createCustomer: boolean, audience?: readonly string[]): Promise<ApplyResult | null>;
  attachments: readonly File[];
  setAttachments(files: readonly File[]): void;
}> {
  const [conversation, setConversation] = useState<ConversationView | null>(null);
  const [job, setJob] = useState<JobView | null>(null);
  const [draft, setDraft] = useState('');
  const [documentType, setDocumentTypeState] = useState<DocumentType>('QUOTATION');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tradeView, setTradeView] = useState<ConversationTradeView | null>(null);
  const tradeInput = tradeView?.input ?? null;
  const [changes, setChanges] = useState<string[]>([]);
  const [appliedDocumentEvent, setAppliedDocumentEvent] = useState<Readonly<{ proposalId: string; documentId: string }> | null>(null);
  const [attachments, setAttachments] = useState<readonly File[]>([]);
  const submissionAttempt = useRef<SubmissionAttempt | null>(null);
  const submissionInFlight = useRef(false);
  const autoApplyInFlight = useRef<string | null>(null);

  const conversationId = conversation?.conversationId ?? null;
  const activeJobId = useMemo(() => conversation?.messages.at(-1)?.jobId ?? null, [conversation?.messages]);
  const proposal = conversation?.proposals.findLast((item) => item.status === 'PENDING' && item.messageId === conversation.messages.at(-1)?.id) ?? null;
  const appliedProposal = conversation?.proposals.findLast((item) => item.status === 'APPLIED') ?? null;
  const jobPending = conversation?.messages.at(-1)?.status === 'QUEUED'
    || job?.jobId === activeJobId && (job?.state === 'QUEUED' || job?.state === 'RUNNING');
  const busy = pending || Boolean(jobPending) || Boolean(proposal && !error);
  const completionInput = useMemo(() => getConversationCompletionInput(tradeView, conversation), [tradeView, conversation]);
  const completion = useMemo(() => completionInput ? getDocumentCompletion(completionInput, documentType, getSkippedFields(conversation?.proposals ?? [], completionInput)) : null, [completionInput, documentType, conversation?.proposals]);

  const loadConversation = useCallback(async (id: string): Promise<void> => {
    const response = await fetch(`/api/conversations/${id}`, { cache: 'no-store' });
    if (!response.ok) throw new Error(await responseMessage(response));
    const value = await response.json() as ConversationView;
    setConversation(value);
    setDocumentTypeState(value.documentType);
    if (value.tradeId) {
      const tradeResponse = await fetch(`/api/trades/${value.tradeId}`, { cache: 'no-store' });
      if (!tradeResponse.ok) throw new Error(await responseMessage(tradeResponse));
      setTradeView(await tradeResponse.json() as ConversationTradeView);
    } else {
      setTradeView(null);
    }
    const latestJobId = value.messages.at(-1)?.jobId;
    if (!latestJobId) {
      setJob(null);
      return;
    }
    const jobResponse = await fetch(`/api/jobs/${latestJobId}`, { cache: 'no-store' });
    if (jobResponse.ok) setJob(await jobResponse.json() as JobView);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const id = new URLSearchParams(window.location.search).get('conversation');
    if (!id) return;
    void Promise.resolve().then(() => loadConversation(id)).catch((caught: unknown) => {
      setError(caught instanceof Error ? caught.message : '会话读取失败。');
    });
  }, [loadConversation]);

  useEffect(() => {
    if (!activeJobId || !conversationId) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = (delay: number): void => {
      if (!disposed) timer = setTimeout(() => void poll(), delay);
    };
    const poll = async (): Promise<void> => {
      if (disposed) return;
      if (typeof document !== 'undefined' && document.hidden) {
        schedule(5_000);
        return;
      }
      try {
        const response = await fetch(`/api/jobs/${activeJobId}`, { cache: 'no-store' });
        if (!response.ok || disposed) {
          schedule(5_000);
          return;
        }
        const next = await response.json() as JobView;
        setJob(next);
        if (next.state === 'QUEUED' || next.state === 'RUNNING') {
          schedule(next.state === 'QUEUED' ? 5_000 : 1_000);
          return;
        }
        await loadConversation(conversationId);
      } catch {
        schedule(5_000);
      }
    };
    const onVisibilityChange = (): void => {
      if (document.hidden) return;
      if (timer) clearTimeout(timer);
      void poll();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    void poll();
    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [activeJobId, conversationId, loadConversation]);

  const submit = useCallback(async (): Promise<void> => {
    const text = draft;
    const diagnostics = { empty: !text.trim() && attachments.length === 0, pending: busy || Boolean(proposal), inFlight: submissionInFlight.current, overLimit: Array.from(text).length > 6_000, hasConversation: Boolean(conversationId) };
    logGenerateDebug('submit.enter', diagnostics);
    if (diagnostics.empty || diagnostics.pending || submissionInFlight.current || diagnostics.overLimit) {
      logGenerateDebug('submit.blocked', diagnostics);
      return;
    }
    submissionInFlight.current = true;
    setPending(true);
    setError(null);
    try {
      let id = conversationId;
      if (!id) {
        logGenerateDebug('submit.request', { step: 'create-conversation', method: 'POST' });
        const create = await fetch('/api/conversations', {
          method: 'POST',
          headers: mutationHeaders(),
          body: JSON.stringify({ documentType }),
        });
        logGenerateDebug('submit.response', { step: 'create-conversation', status: create.status, ok: create.ok });
        if (!create.ok) throw new Error(await responseMessage(create));
        id = (await create.json() as { conversationId: string }).conversationId;
        if (typeof window !== 'undefined') window.history.replaceState({}, '', `/?conversation=${id}`);
        logGenerateDebug('submit.reload', { step: 'after-create' });
        await loadConversation(id);
        logGenerateDebug('submit.reloaded', { step: 'after-create' });
      }

      const previousAttempt = submissionAttempt.current;
      const attachmentIdentity = attachments.map((file) => `${file.name}:${file.size}:${file.lastModified}`).join('|');
      const attempt = previousAttempt?.conversationId === id && previousAttempt.text === text && previousAttempt.attachmentIdentity === attachmentIdentity
        ? previousAttempt
        : { conversationId: id, text, key: crypto.randomUUID(), attachmentIdentity };
      submissionAttempt.current = attempt;
      const form = new FormData();
      form.set('text', text);
      form.set('documentType', documentType);
      for (const attachment of attachments) form.append('attachments', attachment);
      logGenerateDebug('submit.request', { step: 'send-message', method: 'POST' });
      const response = await fetch(`/api/conversations/${id}/messages`, {
        method: 'POST',
        headers: { 'idempotency-key': attempt.key },
        body: form,
      });
      logGenerateDebug('submit.response', { step: 'send-message', status: response.status, ok: response.ok });
      if (!response.ok) throw new Error(await responseMessage(response));
      submissionAttempt.current = null;
      logGenerateDebug('submit.reload', { step: 'after-send' });
      await loadConversation(id);
      logGenerateDebug('submit.queued');
    } catch (caught: unknown) {
      logGenerateDebug('submit.error', { errorType: caught instanceof Error ? caught.name : typeof caught });
      setError(caught instanceof Error ? caught.message : '发送失败，请稍后重试。');
    } finally {
      submissionInFlight.current = false;
      setPending(false);
      logGenerateDebug('submit.finish');
    }
  }, [attachments, conversationId, documentType, draft, loadConversation, busy, proposal]);

  const cancel = useCallback(async (): Promise<void> => {
    if (!activeJobId || !conversationId) return;
    setError(null);
    try {
      const response = await fetch(`/api/jobs/${activeJobId}/cancel`, {
        method: 'POST',
        headers: mutationHeaders(),
        body: '{}',
      });
      if (!response.ok) throw new Error(await responseMessage(response));
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : '任务取消失败。');
    } finally {
      await loadConversation(conversationId).catch(() => undefined);
    }
  }, [activeJobId, conversationId, loadConversation]);

  const retry = useCallback(async (): Promise<void> => {
    const latest = conversation?.messages.at(-1);
    if (!latest) return;
    setDraft(latest.text === '（附件）' ? '' : latest.text);
    setError(null);
  }, [conversation?.messages]);

  const apply = useCallback(async (
    selectedOperationIds: readonly string[],
    createCustomer: boolean,
    audience?: readonly string[],
  ): Promise<ApplyResult | null> => {
    if (!proposal || !conversation) return null;
    let appliedResult: ApplyResult | null = null;
    setPending(true);
    setError(null);
    try {
      let shareScopeVersion = conversation.shareScopeVersion;
      if (conversation.tradeId === null) {
        if (audience === undefined) throw new Error('首次应用前需要确认共享范围。');
        const shareResponse = await fetch(`/api/conversations/${conversation.conversationId}/share-scope`, {
          method: 'PUT',
          headers: mutationHeaders(),
          body: JSON.stringify({
            expectedVersion: conversation.shareScopeVersion,
            messageIds: conversation.messages.map((message) => message.id),
            proposalIds: [proposal.id],
            audience,
          }),
        });
        if (!shareResponse.ok) throw new Error(await responseMessage(shareResponse));
        shareScopeVersion = (await shareResponse.json() as { scopeVersion: number }).scopeVersion;
      }

      const response = await fetch(`/api/proposals/${proposal.id}/apply`, {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({
          selectedOperationIds,
          expectedRevision: conversation.workingRevision,
          shareScopeVersion,
          createCustomer,
        }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      if (tradeInput && Array.isArray(proposal.operationsJson)) {
        const updates = new Map<string, string>();
        for (const operation of proposal.operationsJson as Array<{ operationId: string; field: string; itemId: string | null; value: string | null }>) {
          if (!selectedOperationIds.includes(operation.operationId)) continue;
          const index = operation.itemId ? tradeInput.items.findIndex(item => item.id === operation.itemId) : -1;
          const path = operation.field.startsWith('items.') ? index >= 0 ? `items.${index}.${operation.field.slice(6)}` : null : operation.field;
          if (!path) continue;
          const previous = path.split('.').reduce<unknown>((value, key) => value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined, tradeInput);
          if ((previous ?? null) !== operation.value) updates.set(path, `${completionFieldLabel(path, tradeInput, documentType)}：${previous ?? '未填写'} → ${operation.value ?? '留空'}`);
        }
        setChanges([...updates.values()]);
      }
      setDraft(current => current === conversation.messages.at(-1)?.text || current === '' ? '' : current);
      setAttachments([]);
      appliedResult = await response.json() as ApplyResult;
      return appliedResult;
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : '候选应用失败，请重新读取。');
      return null;
    } finally {
      let refreshed = false;
      await loadConversation(conversation.conversationId).then(() => { refreshed = true; }).catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : '会话重新读取失败。');
      });
      if (refreshed && appliedResult) setAppliedDocumentEvent({ proposalId: proposal.id, documentId: appliedResult.documentId });
      setPending(false);
    }
  }, [conversation, loadConversation, proposal, tradeInput, documentType]);

  const retrySave = useCallback(async (): Promise<void> => {
    if (!proposal || !conversation) return;
    await apply(readOperationIds(proposal.operationsJson), false, conversation.tradeId === null ? [] : undefined);
  }, [apply, proposal, conversation]);

  useEffect(() => {
    if (!proposal || !conversation || autoApplyInFlight.current === proposal.id) return;
    autoApplyInFlight.current = proposal.id;
    queueMicrotask(() => {
      void retrySave();
    });
  }, [retrySave, conversation, proposal]);

  const startNew = useCallback((): void => {
    if (busy) return;
    setConversation(null);
    setJob(null);
    setDraft('');
    setAttachments([]);
    setError(null);
    setTradeView(null);
    setChanges([]);
    setAppliedDocumentEvent(null);
    autoApplyInFlight.current = null;
    submissionAttempt.current = null;
    submissionInFlight.current = false;
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('conversation');
      window.history.replaceState({}, '', `${url.pathname}${url.search}`);
    }
  }, [busy]);

  const consumeAppliedDocumentEvent = useCallback((): void => setAppliedDocumentEvent(null), []);

  return {
    conversation,
    job,
    proposal,
    appliedProposal,
    tradeInput,
    completionInput,
    draft,
    documentType,
    pending: busy,
    error,
    completion,
    changes,
    appliedDocumentEvent,
    consumeAppliedDocumentEvent,
    setDraft,
    setDocumentType: (value) => { if (!conversation) setDocumentTypeState(value); },
    startNew,
    submit,
    cancel,
    retry,
    retrySave,
    apply,
    attachments,
    setAttachments,
  };
}

type ApplyResult = Readonly<{ tradeId: string; documentId: string; revision: number }>;

function readOperationIds(value: unknown): string[] {
  return readJsonArray(value).flatMap((entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return [];
    const operationId = (entry as { operationId?: unknown }).operationId;
    return typeof operationId === 'string' && operationId.length > 0 ? [operationId] : [];
  });
}

function readJsonArray(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}
